import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from 'react-query';
import api from '../../config/axios';
import { 
  ArrowRight,
  Download,
  AlertCircle,
  Loader,
  Eye,
  BookOpen
} from 'lucide-react';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { useToast } from '../../components/common/Toast';
import { useAuth } from '../../contexts/AuthContext';

const FileViewer = () => {
  const { lessonId } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  useAuth();

  const [isDownloading, setIsDownloading] = useState(false);

  // جلب بيانات الدرس
  const { data: lessonData, isLoading, error } = useQuery(
    ['lesson', lessonId],
    async () => {
      const response = await api.get(`/lessons/${lessonId}`);
      return response.data;
    }
  );

  const lesson = useMemo(
    () => lessonData?.data?.lesson || lessonData?.lesson || {},
    [lessonData]
  );

  // التحقق من أن الدرس يحتوي على ملف
  useEffect(() => {
    if (!isLoading && lesson && lesson.content_type !== 'file' && lesson.content_type !== 'pdf') {
      toast.error('هذا الدرس لا يحتوي على ملف');
      navigate(`/student/lessons/${lessonId}`);
    }
  }, [isLoading, lesson, lessonId, navigate, toast]);

  // الحصول على رابط الملف الآمن
  const { data: fileUrlData, isLoading: fileUrlLoading } = useQuery(
    ['file-url', lessonId],
    async () => {
      const response = await api.get(`/lessons/${lessonId}/file-url`);
      return response.data;
    },
    {
      enabled: Boolean(lessonId && !isLoading && lesson)
    }
  );

  const fileUrl = fileUrlData?.fileUrl || fileUrlData?.url || lesson?.video_url || lesson?.file_path;

  const handleDownload = async () => {
    setIsDownloading(true);
    try {
      if (!fileUrl) {
        toast.error('لم نتمكن من الحصول على رابط الملف');
        return;
      }

      // استخدام رابط مباشر بدلاً من axios
      // هذا يسمح للمتصفح بالتعامل مع الملف بشكل صحيح
      const link = document.createElement('a');
      link.href = fileUrl;
      link.download = lesson?.title || 'file';
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast.success('بدء تحميل الملف...');
    } catch (error) {
      toast.error('فشل تحميل الملف');
      console.error('Download error:', error);
    } finally {
      setIsDownloading(false);
    }
  };

  const handleOpenInNewTab = () => {
    if (fileUrl) {
      // فتح الملف مباشرة في نافذة جديدة
      // المتصفح سيقرر تلقائياً هل يعرضه أم يحمله
      window.open(fileUrl, '_blank', 'noopener,noreferrer');
    } else {
      toast.error('لم نتمكن من الحصول على رابط الملف');
    }
  };

  const getFileExtension = () => {
    if (!lesson?.title) return '';
    return lesson.title.split('.').pop()?.toUpperCase() || 'FILE';
  };

  const getFileIcon = () => {
    const ext = getFileExtension();
    if (ext === 'PDF') {
      return '📄';
    } else if (['DOC', 'DOCX'].includes(ext)) {
      return '📝';
    } else if (['XLS', 'XLSX'].includes(ext)) {
      return '📊';
    } else if (['PPT', 'PPTX'].includes(ext)) {
      return '🎬';
    }
    return '📁';
  };

  if (isLoading || fileUrlLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-white">
        <LoadingSpinner size="large" />
        <p className="mt-4 text-gray-500 font-bold animate-pulse">جاري تحضير الملف...</p>
      </div>
    );
  }

  if (error || !lesson) {
    return (
      <div className="max-w-xl mx-auto py-20 px-6 text-center animate-in fade-in zoom-in duration-500">
        <div className="bg-white rounded-[2.5rem] shadow-xl p-10 border border-gray-100">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-6" />
          <h2 className="text-2xl font-black text-gray-900 mb-4">خطأ في تحميل الملف</h2>
          <p className="text-gray-600 mb-8 leading-relaxed">{error?.message || 'عذراً، حدثت مشكلة في تحميل الملف.'}</p>
          <button
            onClick={() => navigate(-1)}
            className="bg-primary-600 text-white px-10 py-3 rounded-2xl font-bold hover:bg-primary-700 transition-all shadow-lg shadow-primary-100 flex items-center justify-center gap-2 mx-auto"
          >
            <ArrowRight size={18} />
            العودة
          </button>
        </div>
      </div>
    );
  }

  const isPdf = lesson?.content_type === 'pdf';

  return (
    <div className="min-h-screen bg-gray-50/30">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-gray-400 hover:text-primary-600 font-bold transition-all group text-sm mb-6"
        >
          <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          العودة
        </button>

        <div className="bg-white rounded-[2rem] p-8 shadow-sm border border-gray-100 relative overflow-hidden group mb-8">
          <div className="absolute top-0 left-0 w-2 h-full bg-primary-600 opacity-20 group-hover:opacity-100 transition-opacity"></div>
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6 relative z-10">
            <div className="space-y-3">
              <div className="flex flex-wrap items-center gap-2">
                <span className="bg-amber-50 text-amber-600 px-3 py-1 rounded-lg text-[10px] font-black uppercase tracking-widest border border-amber-100">
                  📎 ملف مرفق
                </span>
              </div>
              <h1 className="text-3xl font-black text-gray-900">{lesson?.title}</h1>
              <p className="text-gray-600 text-lg font-medium">{lesson?.description}</p>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="grid lg:grid-cols-12 gap-8">
          <div className="lg:col-span-8">
            {/* File Display Area */}
            <div className="bg-white rounded-[2.5rem] shadow-2xl shadow-gray-200/50 border border-gray-100 overflow-hidden">
              <div className="p-8 sm:p-12 min-h-[500px] bg-gradient-to-br from-amber-50 via-white to-orange-50 flex flex-col items-center justify-center">
                {/* File Icon */}
                <div className="text-8xl mb-6 animate-bounce">{getFileIcon()}</div>

                {/* File Info */}
                <div className="text-center space-y-4 max-w-md">
                  <h2 className="text-2xl font-black text-gray-900">{lesson?.title}</h2>
                  <p className="text-gray-600 text-sm font-medium">
                    {isPdf ? 'اضغط على "عرض الملف" لفتح ملف PDF في متصفحك' : 'قم بتحميل الملف والعمل عليه محلياً'}
                  </p>

                  {/* File Extensions Info */}
                  {!isPdf && (
                    <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 text-xs text-blue-700 font-medium">
                      <p className="font-black mb-2">📝 ملفات مدعومة:</p>
                      <p>PDF, Word (.doc, .docx), Excel (.xls, .xlsx), PowerPoint (.ppt, .pptx), وملفات نصية أخرى</p>
                    </div>
                  )}
                </div>

                {/* Action Buttons */}
                <div className="mt-8 flex flex-col sm:flex-row gap-4 w-full max-w-md">
                  {isPdf && (
                    <button
                      onClick={handleOpenInNewTab}
                      disabled={!fileUrl || fileUrlLoading}
                      className="flex-1 bg-primary-600 hover:bg-primary-700 disabled:bg-gray-300 text-white px-6 py-4 rounded-2xl font-bold transition-all flex items-center justify-center gap-2 shadow-lg shadow-primary-100"
                    >
                      <Eye size={20} />
                      عرض الملف
                    </button>
                  )}

                  <button
                    onClick={handleDownload}
                    disabled={!fileUrl || fileUrlLoading || isDownloading}
                    className="flex-1 bg-amber-600 hover:bg-amber-700 disabled:bg-gray-300 text-white px-6 py-4 rounded-2xl font-bold transition-all flex items-center justify-center gap-2 shadow-lg shadow-amber-100"
                  >
                    {isDownloading ? (
                      <>
                        <Loader size={20} className="animate-spin" />
                        جاري التحميل...
                      </>
                    ) : (
                      <>
                        <Download size={20} />
                        تحميل الملف
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>

            {/* Description */}
            {lesson?.description && (
              <div className="bg-white rounded-[2.2rem] p-10 border border-gray-100 shadow-sm relative overflow-hidden group mt-8">
                <div className="absolute top-0 right-0 w-32 h-32 bg-primary-50 rounded-bl-full -z-0 opacity-40 group-hover:scale-110 transition-transform"></div>
                <h2 className="text-2xl font-black text-gray-900 mb-6 flex items-center gap-3 relative z-10">
                  <div className="w-1.5 h-6 bg-primary-600 rounded-full"></div>
                  شرح الملف
                </h2>
                <p className="text-gray-600 leading-relaxed text-lg font-medium relative z-10 whitespace-pre-line">
                  {lesson.description}
                </p>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-4">
            <div className="bg-white rounded-[2.2rem] p-8 border border-gray-100 shadow-sm sticky top-8 space-y-6">
              <div className="space-y-4">
                <h3 className="text-lg font-black text-gray-900 flex items-center gap-2">
                  <BookOpen size={20} className="text-primary-600" />
                  معلومات الملف
                </h3>
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                    <span className="text-sm font-medium text-gray-600">النوع:</span>
                    <span className="font-bold text-gray-900">{getFileExtension()}</span>
                  </div>
                  {lesson?.file_size && (
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                      <span className="text-sm font-medium text-gray-600">الحجم:</span>
                      <span className="font-bold text-gray-900">
                        {(lesson.file_size / (1024 * 1024)).toFixed(2)} MB
                      </span>
                    </div>
                  )}
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                    <span className="text-sm font-medium text-gray-600">المسار:</span>
                    <span className="font-bold text-gray-900 text-right">{lesson?.title}</span>
                  </div>
                </div>
              </div>

              {/* Tips */}
              <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 space-y-2">
                <p className="text-xs font-black text-blue-900 uppercase tracking-wider">💡 نصيحة</p>
                <p className="text-xs text-blue-700 leading-relaxed font-medium">
                  {isPdf
                    ? 'يمكنك فتح ملف PDF مباشرة في المتصفح أو تحميله على جهازك للعمل عليه لاحقاً'
                    : 'قم بتحميل الملف وفتحه باستخدام التطبيق المناسب (Word، Excel، إلخ) على جهازك'
                  }
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FileViewer;
